import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { ausgabenApi } from '../api';

const FARBEN = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

export default function AusgabenAuswertungPage() {
  const [daten, setDaten] = useState(null);
  const [filterJahr, setFilterJahr] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const jahre = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => { laden(); }, [filterJahr]);

  async function laden() {
    setLoading(true);
    try {
      const res = await ausgabenApi.auswertung(filterJahr);
      setDaten(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function exportSteuer() {
    window.open(ausgabenApi.exportUrl({ jahr: filterJahr, steuerrelevant: 1 }));
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Lade Auswertung...</div>;
  if (!daten) return null;

  const jahrDaten = daten.summeProJahr.find(j => j.steuerjahr === filterJahr);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px' }}>📊 Ausgaben-Auswertung</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select value={filterJahr} onChange={e => setFilterJahr(parseInt(e.target.value))}
            style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '600' }}>
            {jahre.map(j => <option key={j} value={j}>{j}</option>)}
          </select>
          <button onClick={exportSteuer} style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
            📥 Steuerrelevant exportieren
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Gesamtausgaben ' + filterJahr, wert: jahrDaten?.gesamt || 0, icon: '💰', farbe: '#6366f1' },
          { label: 'Anzahl Ausgaben', wert: jahrDaten?.anzahl || 0, icon: '🧾', farbe: '#3b82f6', istAnzahl: true },
          { label: 'Steuerrelevant', wert: daten.steuerrelevantSumme?.gesamt || 0, icon: '✅', farbe: '#10b981' },
          { label: 'Steuerrelevante Posten', wert: daten.steuerrelevantSumme?.anzahl || 0, icon: '📋', farbe: '#f59e0b', istAnzahl: true },
        ].map((k, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${k.farbe}` }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{k.icon}</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: k.farbe }}>
              {k.istAnzahl ? k.wert : k.wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
            <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>📅 Ausgaben pro Jahr</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={daten.summeProJahr}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="steuerjahr" />
              <YAxis tickFormatter={v => v + '€'} />
              <Tooltip formatter={v => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} />
              <Bar dataKey="gesamt" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>🏷️ Nach Kategorie ({filterJahr})</h3>
          {daten.summeProKategorie.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Keine Daten</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={daten.summeProKategorie} dataKey="gesamt" nameKey="kategorie" cx="50%" cy="50%" outerRadius={80}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                  {daten.summeProKategorie.map((_, i) => <Cell key={i} fill={FARBEN[i % FARBEN.length]} />)}
                </Pie>
                <Tooltip formatter={v => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e293b' }}>✅ Steuerrelevante Ausgaben {filterJahr}</h3>
        {daten.steuerrelevant.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>Keine steuerrelevanten Ausgaben</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Bezeichnung', 'Kategorie', 'Kaufdatum', 'Betrag'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daten.steuerrelevant.map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px', fontWeight: '500' }}>{a.bezeichnung}</td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.kategorie}</td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>{new Date(a.kaufdatum).toLocaleDateString('de-DE')}</td>
                  <td style={{ padding: '10px 14px', fontWeight: '700', color: '#10b981' }}>{a.betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                <td colSpan="3" style={{ padding: '10px 14px', fontWeight: '700' }}>Gesamt steuerrelevant</td>
                <td style={{ padding: '10px 14px', fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                  {(daten.steuerrelevantSumme?.gesamt || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
